// Fleet Reports — Fuel Cost section (P3, 2026-07-22).
// Fuel cost = consumption × the licence's price-per-litre. The backend already
// did the multiply and carries the currency; here we just render it (lower is
// better). Absence stays "No data" — a null cost is never shown as a 0.
import React from "react";
import { Card, Grid } from "@mui/material";
import FleetBar from "./charts/FleetBar";
import FleetLine from "./charts/FleetLine";
import StatTile from "./StatTile";
import { RAG, ragForSeries, ragBg } from "./ragColors";

const VEHICLE_COLORS = ["#1f77b4", "#ff7f0e", "#2ca02c", "#d62728", "#9467bd"];

const sectionTitle = (text) => (
  <p style={{ fontSize: "15px", fontWeight: "bold", color: "#333333", margin: "0 0 12px 0", textTransform: "uppercase" }}>
    {text}
  </p>
);

export default function FuelCostSection({ report }) {
  const m = (report && report.metrics && report.metrics.fuelCost) || null;
  const currency = (m && m.currency) || "USD";
  const fmt = (v, d = 2) =>
    v === null || v === undefined || Number.isNaN(v) ? "No data" : `${Number(v).toFixed(d)} ${currency}`;

  const weekRows = (m && m.perVehicleWeek) || [];
  const ragWeek = React.useMemo(() => ragForSeries(weekRows, false), [weekRows]);
  const barData = React.useMemo(
    () => weekRows.map((r) => ({ key: r.sensorId, label: r.name || r.sensorId, value: r.value, color: ragWeek(r.value) })),
    [weekRows, ragWeek]
  );

  const days = React.useMemo(() => ((m && m.fleetDay) || []).map((d) => d.date), [m]);
  const lineSeries = React.useMemo(() => {
    if (!m) return [];
    return ((report && report.vehicles) || []).map((v, i) => ({
      key: v.sensorId,
      label: v.name || v.sensorId,
      color: VEHICLE_COLORS[i % VEHICLE_COLORS.length],
      points: (m.perVehicleDay || [])
        .filter((r) => r.sensorId === v.sensorId)
        .reduce((acc, r) => {
          acc[r.date] = r.value;
          return acc;
        }, {}),
    }));
  }, [m, report]);

  // No price configured for this tenant ⇒ the backend omits the metric entirely.
  if (!m) {
    return (
      <Card style={{ padding: "40px 20px", textAlign: "center" }}>
        <p style={{ fontSize: "15px", color: "#c7c7c7", margin: 0 }}>
          No fuel price is set for this fleet - enter one on the <b>Settings</b> page, then Load the report again.
        </p>
      </Card>
    );
  }

  return (
    <div>
      <Grid container spacing={2} style={{ marginBottom: "20px" }}>
        <Grid item xs={12} sm={4} md={3}>
          <StatTile label="Fleet Fuel Cost" value={m.fleetWeek} unit={currency} better="lower" decimals={2} />
        </Grid>
        <Grid item xs={12} sm={8} md={9}>
          <Card style={{ padding: "16px", height: "100%" }}>
            {sectionTitle(`Per vehicle (${currency})`)}
            <FleetBar data={barData} unit={currency} height="240px" />
          </Card>
        </Grid>
      </Grid>

      <Card style={{ padding: "16px", marginBottom: "20px" }}>
        {sectionTitle(`Daily per vehicle (${currency})`)}
        <FleetLine days={days} series={lineSeries} unit={currency} height="300px" />
      </Card>

      <Card style={{ padding: "16px" }}>
        {sectionTitle(`Totals (${currency})`)}
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
            <thead>
              <tr style={{ background: "#e2e2e2" }}>
                <th style={{ textAlign: "left", padding: "10px" }}>Vehicle</th>
                <th style={{ textAlign: "right", padding: "10px" }}>Total cost ({currency})</th>
              </tr>
            </thead>
            <tbody>
              {weekRows.map((r) => {
                const c = ragWeek(r.value);
                return (
                  <tr key={r.sensorId} style={{ borderBottom: "1px solid #eeeeee" }}>
                    <td style={{ padding: "10px", fontWeight: "bold" }}>{r.name || r.sensorId}</td>
                    <td style={{ padding: "10px", textAlign: "right", backgroundColor: ragBg(c), color: r.value === null ? RAG.neutral : "#333333" }}>
                      {fmt(r.value)}
                    </td>
                  </tr>
                );
              })}
              <tr style={{ background: "#f5f5f5", fontWeight: "bold" }}>
                <td style={{ padding: "10px" }}>Fleet</td>
                <td style={{ padding: "10px", textAlign: "right" }}>{fmt(m.fleetWeek)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p style={{ fontSize: "13px", color: "grey", marginTop: "12px", marginBottom: 0 }}>
          <b>Hint:</b> Cost is fuel consumption × {m.pricePerLitre != null ? `${Number(m.pricePerLitre).toFixed(2)} ${currency}` : "the configured price"} per litre. It
          follows the same reset-aware consumption figure shown on the Fuel Consumption page.
        </p>
      </Card>
    </div>
  );
}
